import { BellIcon as BellIconOutline } from '@heroicons/react/24/outline';
import { AtSign, MessageSquare, MoreVertical, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  ClientConversation,
  NotificationSchema,
  NotificationTab,
} from '@/services/api/notificationsService';
import {
  useClientConversationsListQuery,
  useNotificationListQuery,
  useNotificationMarkAllReadMutation,
  useNotificationMarkReadMutation,
} from '@/services/api/notificationsService';

import { MessageListItem } from './MessageListItem';
import { NotificationListItem } from './NotificationListItem';
import type { NotificationNavTab } from './NotificationNav';

interface NotificationListProps {
  tab: NotificationNavTab;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

type FilterMode = 'all' | 'unread';
type MessageFilterMode = 'all' | 'unreplied';

export const NotificationList: React.FC<NotificationListProps> = ({
  tab,
  selectedId,
  onSelect,
}) => {
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [messageFilterMode, setMessageFilterMode] = useState<MessageFilterMode>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const previousTabRef = useRef(tab);
  const listRef = useRef<HTMLDivElement>(null);

  // Handle tab changes - clear content immediately for smooth transition
  useEffect(() => {
    if (previousTabRef.current !== tab) {
      setIsTabTransitioning(true);
      setSearchQuery('');
      setDebouncedSearch('');
      setFilterMode('all');
      setMessageFilterMode('all');
      previousTabRef.current = tab;

      // Brief delay to ensure clean transition
      const timer = setTimeout(() => {
        setIsTabTransitioning(false);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [tab]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const apiTab: NotificationTab = tab;
  const isMessagesTab = tab === 'messages';

  // Fetch notifications with 10s polling (for mentions/updates tabs)
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    isFetching: notificationsFetching,
  } = useNotificationListQuery(
    {
      tab: apiTab,
      search: debouncedSearch || undefined,
      includeCounts: true,
      maxReadNotifications: 50,
      unreadOnly: filterMode === 'unread',
    },
    { pollingInterval: 60000, skip: isMessagesTab },
  );

  // Fetch client conversations with 1min polling (for messages tab)
  const {
    data: conversationsData,
    isLoading: conversationsLoading,
    isFetching: conversationsFetching,
  } = useClientConversationsListQuery(
    {
      search: debouncedSearch || undefined,
      unreplied: messageFilterMode === 'unreplied' || undefined,
      limit: 50,
    },
    { pollingInterval: 60000, skip: !isMessagesTab },
  );

  const [markRead] = useNotificationMarkReadMutation();
  const [markAllRead] = useNotificationMarkAllReadMutation();

  // Get appropriate data based on tab - clear during transitions
  const notifications = React.useMemo(
    () => (isTabTransitioning ? [] : notificationsData?.notifications || []),
    [notificationsData?.notifications, isTabTransitioning],
  );
  const conversations = React.useMemo(
    () => (isTabTransitioning ? [] : conversationsData?.conversations || []),
    [conversationsData?.conversations, isTabTransitioning],
  );
  const isLoading = isTabTransitioning || (isMessagesTab ? conversationsLoading : notificationsLoading);
  const isFetching = isMessagesTab ? conversationsFetching : notificationsFetching;
  const unreadCount = isMessagesTab
    ? conversationsData?.total_unread_count || 0
    : notificationsData?.unread_count || 0;

  const handleMarkAllRead = () => {
    // For messages tab, this marks all SMS notifications as read
    // For other tabs, marks notifications in that category as read
    markAllRead({ tab: apiTab });
  };

  const handleMarkRead = (notification: NotificationSchema, markedUnread: boolean) => {
    markRead({
      id: notification.id,
      markReadSchema: { marked_unread: markedUnread },
    });
  };

  // Select notification and mark as read
  const handleSelectNotification = useCallback(
    (notification: NotificationSchema) => {
      onSelect(notification.id);
      // Mark as read if unread
      if (!notification.read_at || notification.marked_unread) {
        markRead({
          id: notification.id,
          markReadSchema: { marked_unread: false },
        });
      }
    },
    [onSelect, markRead],
  );

  // Select conversation - show chat detail panel
  const handleSelectConversation = useCallback(
    (conversation: ClientConversation) => {
      // Select the conversation by matter_id to show chat detail
      onSelect(conversation.matter_id);
    },
    [onSelect],
  );

  // Keyboard navigation for notifications
  useEffect(() => {
    if (isMessagesTab) {
      // Disable keyboard nav for messages tab for now
      return undefined;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (notifications.length === 0) return;

      // Only handle arrow keys
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

      // Don't interfere with input fields
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      e.preventDefault();

      const currentIndex = selectedId
        ? notifications.findIndex((n) => n.id === selectedId)
        : -1;

      let newIndex: number;
      if (e.key === 'ArrowDown') {
        newIndex = currentIndex < notifications.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : notifications.length - 1;
      }

      const newNotification = notifications[newIndex];
      if (newNotification) {
        handleSelectNotification(newNotification);

        // Scroll the selected item into view
        const listElement = listRef.current;
        if (listElement) {
          const items = listElement.querySelectorAll('[data-notification-item]');
          const targetItem = items[newIndex] as HTMLElement | undefined;
          targetItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [notifications, selectedId, handleSelectNotification, isMessagesTab]);

  const getTabLabel = () => {
    if (tab === 'messages') return 'Messages';
    if (tab === 'mentions') return 'Mentions';
    if (tab === 'subscribed') return 'Subscribed';
    return 'Updates';
  };
  const tabLabel = getTabLabel();

  const hasItems = isMessagesTab ? conversations.length > 0 : notifications.length > 0;

  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">{tabLabel}</h2>
            {isFetching && !isLoading && (
              <div className="w-2.5 h-2.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          {tab === 'messages' && (
            <p className="text-[10px] text-gray-400 font-medium">
              Client SMS · Email coming soon
            </p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[140px] rounded-lg shadow-lg border-0 ring-1 ring-black/5 p-1">
            <DropdownMenuItem
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
              className="text-xs font-medium text-gray-700 rounded-md px-2.5 py-1.5 cursor-pointer focus:bg-gray-100"
            >
              Mark all as read
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.currentTarget.blur();
              }
            }}
            className="w-full pl-8 pr-7 py-1.5 text-sm font-medium bg-gray-100 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-500"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Filter pills for messages tab */}
      {isMessagesTab && (
        <div className="flex items-center gap-1.5 px-3 pb-2">
          <button
            type="button"
            onClick={() => setMessageFilterMode('all')}
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              messageFilterMode === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setMessageFilterMode('unreplied')}
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              messageFilterMode === 'unreplied'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Unreplied
          </button>
        </div>
      )}

      {/* Filter pills for notifications tabs */}
      {!isMessagesTab && (
        <div className="flex items-center gap-1.5 px-3 pb-2">
          <button
            type="button"
            onClick={() => setFilterMode('all')}
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              filterMode === 'all'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('unread')}
            className={cn(
              'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
              filterMode === 'unread'
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            Unread{unreadCount > 0 && ` (${unreadCount})`}
          </button>
        </div>
      )}

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-12 animate-in fade-in duration-150">
            <div className="flex flex-col items-center gap-2">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent" />
              <span className="text-xs text-gray-400">Loading...</span>
            </div>
          </div>
        )}

        {!isLoading && !hasItems && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 px-4 animate-in fade-in duration-200">
            {tab === 'messages' && (
              <MessageSquare className="w-10 h-10 text-gray-400 mb-3" />
            )}
            {tab === 'mentions' && (
              <AtSign className="w-10 h-10 text-gray-400 mb-3" />
            )}
            {tab === 'updates' && (
              <BellIconOutline className="w-10 h-10 text-gray-400 mb-3" />
            )}
            {debouncedSearch && (
              <p className="text-sm font-medium">No results match your search</p>
            )}
            {!debouncedSearch && filterMode === 'unread' && !isMessagesTab && (
              <p className="text-sm font-medium">No unread notifications</p>
            )}
            {!debouncedSearch && messageFilterMode === 'unreplied' && isMessagesTab && (
              <>
                <p className="text-sm font-medium">No unreplied messages</p>
                <p className="text-xs text-gray-400 mt-1 text-center">
                  All client messages have been replied to
                </p>
              </>
            )}
            {!debouncedSearch && messageFilterMode !== 'unreplied' && tab === 'messages' && (
              <>
                <p className="text-sm font-medium">No client texts yet</p>
                <p className="text-xs text-gray-400 mt-1 text-center">
                  SMS from clients on matters you manage will appear here
                </p>
              </>
            )}
            {!debouncedSearch && filterMode !== 'unread' && tab === 'mentions' && (
              <p className="text-sm font-medium">No mentions yet</p>
            )}
            {!debouncedSearch && filterMode !== 'unread' && tab === 'updates' && (
              <p className="text-sm font-medium">No updates yet</p>
            )}
          </div>
        )}

        {/* Conversations list */}
        {!isLoading && isMessagesTab && conversations.length > 0 && (
          <div className="divide-y divide-gray-100 animate-in fade-in duration-200">
            {conversations.map((conversation) => (
              <MessageListItem
                key={conversation.matter_id}
                conversation={conversation}
                isSelected={conversation.matter_id === selectedId}
                onSelect={() => handleSelectConversation(conversation)}
              />
            ))}
          </div>
        )}

        {/* Notifications list */}
        {!isLoading && !isMessagesTab && notifications.length > 0 && (
          <div className="divide-y divide-gray-100 animate-in fade-in duration-200">
            {notifications.map((notification) => (
              <NotificationListItem
                key={notification.id}
                notification={notification}
                isSelected={notification.id === selectedId}
                onSelect={() => handleSelectNotification(notification)}
                onMarkRead={(markedUnread) => handleMarkRead(notification, markedUnread)}
                showIcon={tab !== 'mentions'}
                data-notification-item
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
